import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Button,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner
} from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_BASE_CANDIDATES, SITE_BASE_CANDIDATES } from '../../apiConfig';
import StudentThemeToggle from './StudentThemeToggle';

const OILA_OPTIONS = [
  { value: '0', label: '-- Tanlang --' },
  { value: '1', label: 'Uylanmagan / turmushga chiqmagan' },
  { value: '2', label: 'Uylangan / turmushga chiqqan' }
];

const XARAKTER_OPTIONS = [
  { value: '0', label: '-- Tanlang --' },
  { value: '1', label: "Og'ir" },
  { value: '2', label: "Sho'x" },
  { value: '3', label: 'Qiziqqon' },
  { value: '4', label: 'Quvnoq' },
  { value: '5', label: 'Qaysar' }
];

const SHAPE_OPTIONS = [
  { value: '1', label: '1-shakl', asset: '/student-assets/rasm/4.jpg' },
  { value: '2', label: '2-shakl', asset: '/student-assets/rasm/3.jpg' },
  { value: '3', label: '3-shakl', asset: '/student-assets/rasm/aylana.jpg' },
  { value: '4', label: '4-shakl', asset: '/student-assets/rasm/z.jpg' },
  { value: '5', label: '5-shakl', asset: '/student-assets/rasm/tort.jpg' }
];

const DEFAULT_FORM_STATE = {
  talaba_id: '',
  passport: '',
  full_name: '',
  faculty: '',
  group: '',
  birth_date: '',
  address: '',
  image_url: '',
  oila: '0',
  bitirgan_nomi: '',
  ota_ona_manzil: '',
  otasi_fio: '',
  onasi_fio: '',
  lavozim: '',
  harbiy_qism: '',
  yashash_manzil: '',
  shakl: '0',
  xarakter: '0',
  rang: '',
  salomatlik: '',
  ozlashtirish: '',
  munosabat: '',
  kursdosh: ''
};

const toFormState = student => ({
  talaba_id: student && student.talaba_id ? String(student.talaba_id) : '',
  passport: (student && student.passport) || '',
  full_name: (student && student.full_name) || '',
  faculty: (student && student.faculty) || '',
  group: (student && student.group) || '',
  birth_date: (student && student.birth_date) || '',
  address: (student && student.address) || '',
  image_url: (student && student.image_url) || '',
  oila: String((student && student.oila) || 0),
  bitirgan_nomi: (student && student.bitirgan_nomi) || '',
  ota_ona_manzil: (student && student.ota_ona_manzil) || '',
  otasi_fio: (student && student.otasi_fio) || '',
  onasi_fio: (student && student.onasi_fio) || '',
  lavozim: (student && student.lavozim) || '',
  harbiy_qism: (student && student.harbiy_qism) || '',
  yashash_manzil: (student && student.yashash_manzil) || '',
  shakl: String((student && student.shakl) || 0),
  xarakter: String((student && student.xarakter) || 0),
  rang: (student && student.rang) || '',
  salomatlik: (student && student.salomatlik) || '',
  ozlashtirish: (student && student.ozlashtirish) || '',
  munosabat: (student && student.munosabat) || '',
  kursdosh: (student && student.kursdosh) || ''
});

const StudentPassportPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const logoutTimerRef = useRef(null);

  const siteBase = SITE_BASE_CANDIDATES[0] || 'https://psixologiya.suvoriy.com/';
  const loadEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/passport`),
    []
  );
  const saveEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/passport/save`),
    []
  );
  const logoutEndpointCandidates = useMemo(
    () => API_BASE_CANDIDATES.map(base => `${base}/test/logout`),
    []
  );

  const resolveAsset = asset => {
    if (!asset) {
      return '';
    }

    if (/^https?:\/\//i.test(asset)) {
      return asset;
    }

    if (asset.startsWith('/')) {
      return `${siteBase}${asset}`;
    }

    return `${siteBase}/${asset.replace(/^\/+/, '')}`;
  };

  const photoUrl = imagePreview || resolveAsset(formState.image_url) || resolveAsset('/rasm/talaba/rasm-mavjud-emas.jpg');

  const filledCount = [
    formState.oila !== '0',
    !!formState.bitirgan_nomi,
    !!formState.ota_ona_manzil,
    !!formState.otasi_fio,
    !!formState.onasi_fio,
    !!formState.lavozim,
    !!formState.harbiy_qism,
    !!formState.yashash_manzil,
    formState.shakl !== '0',
    formState.xarakter !== '0',
    !!formState.rang,
    !!formState.salomatlik,
    !!formState.ozlashtirish,
    !!formState.munosabat,
    !!formState.kursdosh
  ].filter(Boolean).length;

  const submitRequest = async (candidates, buildRequest) => {
    let lastError = null;

    for (const endpoint of candidates) {
      try {
        return await buildRequest(endpoint);
      } catch (error) {
        lastError = error;
        const status = error.response?.status || 0;
        if (status && ![404, 502, 503, 504].includes(status)) {
          throw error;
        }
      }
    }

    throw lastError;
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const response = await submitRequest(loadEndpointCandidates, endpoint => {
          const body = new URLSearchParams();
          return axios.post(endpoint, body, {
            timeout: 15000,
            withCredentials: true
          });
        });

        if (response.data && response.data.success && response.data.student) {
          setFormState(toFormState(response.data.student));
          return;
        }

        throw new Error((response.data && response.data.message) || "Talaba pasporti yuklanmadi.");
      } catch (error) {
        if (error.response?.data?.auth_required) {
          history.replace('/authentication/basic/login');
          return;
        }

        setErrorMessage(
          error.message || "Talaba pasportini yuklashda xatolik yuz berdi."
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [history, loadEndpointCandidates]);

  useEffect(
    () => () => {
      if (logoutTimerRef.current) {
        window.clearTimeout(logoutTimerRef.current);
      }
    },
    []
  );

  const handleFieldChange = ({ target }) => {
    const { name, value } = target;
    setFormState(current => ({
      ...current,
      [name]: value
    }));
  };

  const handleImageChange = event => {
    const file = event.target.files && event.target.files[0];

    if (!file) {
      return;
    }

    if (file.size > 1048576) {
      toast.error("Rasm hajmi 1 MB dan yuqori bo`lmasligi kerak.");
      event.target.value = '';
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (logoutPending) {
      return;
    }

    if (!formState.talaba_id) {
      setErrorMessage("Talaba ma`lumoti topilmadi. Kirish oynasidan qayta kiring.");
      return;
    }

    setSaving(true);
    setErrorMessage('');

    try {
      const response = await submitRequest(saveEndpointCandidates, endpoint => {
        const body = new FormData();
        body.append('talaba_id', formState.talaba_id);
        body.append('oila', formState.oila);
        body.append('bitirgan_nomi', formState.bitirgan_nomi);
        body.append('ota_ona_manzil', formState.ota_ona_manzil);
        body.append('otasi_fio', formState.otasi_fio);
        body.append('onasi_fio', formState.onasi_fio);
        body.append('lavozim', formState.lavozim);
        body.append('harbiy_qism', formState.harbiy_qism);
        body.append('yashash_manzil', formState.yashash_manzil);
        body.append('xarakter', formState.xarakter);
        body.append('rang', formState.rang);
        body.append('shakl', formState.shakl);
        body.append('salomatlik', formState.salomatlik);
        body.append('ozlashtirish', formState.ozlashtirish);
        body.append('munosabat', formState.munosabat);
        body.append('kursdosh', formState.kursdosh);

        if (imageFile) {
          body.append('rasm', imageFile);
        }

        return axios.post(endpoint, body, {
          timeout: 20000,
          withCredentials: true
        });
      });

      if (response.data && response.data.malumot) {
        toast.success((response.data.message || "Psixologik pasport saqlandi.") + ' 2.5 soniyadan keyin tizimdan chiqiladi.');
        setImageFile(null);
        setImagePreview('');
        setLogoutPending(true);
        logoutTimerRef.current = window.setTimeout(async () => {
          try {
            await submitRequest(logoutEndpointCandidates, endpoint =>
              axios.post(
                endpoint,
                {},
                {
                  timeout: 10000,
                  withCredentials: true
                }
              )
            );
          } catch (logoutError) {
            // Frontend tarafdagi logout baribir davom etadi.
          } finally {
            history.replace('/authentication/basic/login');
          }
        }, 2500);
        return;
      }

      if (response.data && response.data.tolmadi) {
        throw new Error("Kamida bitta maydonni to`ldirish kerak.");
      }

      throw new Error((response.data && response.data.message) || "Pasportni saqlashda xatolik yuz berdi.");
    } catch (error) {
      if (error.response?.data?.auth_required) {
        history.replace('/authentication/basic/login');
        return;
      }

      const message = error.message || "Pasportni saqlashda xatolik yuz berdi.";
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleBackToLogin = () => {
    if (logoutPending) {
      return;
    }

    handleExit();
  };

  const handleExit = () => {
    if (logoutPending) {
      return;
    }

    submitRequest(logoutEndpointCandidates, endpoint =>
      axios.post(
        endpoint,
        {},
        {
          timeout: 10000,
          withCredentials: true
        }
      )
    )
      .catch(() => null)
      .finally(() => {
        history.replace('/authentication/basic/login');
      });
  };

  return (
    <div className="psi-auth-stack psi-auth-stack--passport">
      <div className="psi-login-panel psi-login-panel--passport">
        <div className="psi-student-passport__topbar">
          <div>
            <div className="psi-student-passport__eyebrow">Talaba uchun</div>
            <h1 className="psi-student-passport__title">Psixologik pasport</h1>
            <p className="psi-student-passport__subtitle">
              Ma`lumotlarni yangilab saqlang. Pasport to`ldirilishi talaba profilini shakllantirishda ishlatiladi.
            </p>
          </div>
          <div className="psi-student-passport__topbar-tools">
            <StudentThemeToggle />
            <div className="psi-student-passport__topbar-actions">
              <Button color="light" className="psi-student-passport__ghost" onClick={handleBackToLogin} disabled={logoutPending}>
                Login sahifasi
              </Button>
              <Button color="light" className="psi-student-passport__ghost" onClick={handleExit} disabled={logoutPending}>
                Chiqish
              </Button>
            </div>
          </div>
        </div>

        {errorMessage ? (
          <Alert color="danger" className="psi-login-alert mt-3">
            {errorMessage}
          </Alert>
        ) : null}

        {loading ? (
          <div className="psi-student-passport__loading">
            <Spinner color="light" />
            <span>Talaba pasporti yuklanmoqda...</span>
          </div>
        ) : (
          <>
            <div className="psi-student-passport__metrics">
              <div className="psi-student-passport__metric">
                <strong>{filledCount}/15</strong>
                <span>To`ldirilgan bandlar</span>
              </div>
              <div className="psi-student-passport__metric">
                <strong>{formState.passport || 'Mavjud emas'}</strong>
                <span>Hemis ID</span>
              </div>
              <div className="psi-student-passport__metric">
                <Badge pill className="psi-passport-state psi-passport-state--success">
                  Talaba profili
                </Badge>
              </div>
            </div>

            <div className="psi-passport-profile psi-student-passport__profile">
              <div>
                <div className="psi-passport-section mb-4">
                  <h6 className="psi-passport-section__title">Asosiy ma`lumot</h6>
                  <Row form>
                    <Col md="6">
                      <FormGroup>
                        <Label>F.I.Sh</Label>
                        <Input value={formState.full_name} disabled className="psi-passport-readonly" />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Fakultet va guruh</Label>
                        <Input value={`${formState.faculty} / ${formState.group}`} disabled className="psi-passport-readonly" />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Tug`ilgan sana</Label>
                        <Input value={formState.birth_date} disabled className="psi-passport-readonly" />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Doimiy manzil</Label>
                        <Input value={formState.address} disabled className="psi-passport-readonly" />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>

                <div className="psi-passport-section">
                  <h6 className="psi-passport-section__title">Pasport formasi</h6>
                  <Row form>
                    <Col md="6">
                      <FormGroup>
                        <Label for="oila">Oilaviy ahvoli</Label>
                        <Input
                          id="oila"
                          name="oila"
                          type="select"
                          value={formState.oila}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        >
                          {OILA_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="bitirgan_nomi">Institutgacha tamomlagan o`quv yurti</Label>
                        <Input
                          id="bitirgan_nomi"
                          name="bitirgan_nomi"
                          value={formState.bitirgan_nomi}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label for="ota_ona_manzil">Oila a`zolari haqida ma`lumot</Label>
                        <Input
                          id="ota_ona_manzil"
                          name="ota_ona_manzil"
                          type="textarea"
                          rows="3"
                          value={formState.ota_ona_manzil}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="otasi_fio">Otasi F.I.Sh / telefon</Label>
                        <Input
                          id="otasi_fio"
                          name="otasi_fio"
                          value={formState.otasi_fio}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="onasi_fio">Onasi F.I.Sh / telefon</Label>
                        <Input
                          id="onasi_fio"
                          name="onasi_fio"
                          value={formState.onasi_fio}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label for="lavozim">Institutgacha ishlagan joyi, lavozimi, staji</Label>
                        <Input
                          id="lavozim"
                          name="lavozim"
                          type="textarea"
                          rows="3"
                          value={formState.lavozim}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="harbiy_qism">Harbiy xizmat ma`lumoti</Label>
                        <Input
                          id="harbiy_qism"
                          name="harbiy_qism"
                          value={formState.harbiy_qism}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="yashash_manzil">Yashash sharoiti va manzil</Label>
                        <Input
                          id="yashash_manzil"
                          name="yashash_manzil"
                          value={formState.yashash_manzil}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="12">
                      <FormGroup>
                        <Label>Shakl tanlovi</Label>
                        <div className="psi-passport-shape-grid">
                          {SHAPE_OPTIONS.map(option => (
                            <label
                              key={option.value}
                              className={`psi-passport-shape-option ${formState.shakl === option.value ? 'is-active' : ''}`}
                            >
                              <input
                                type="radio"
                                name="shakl"
                                value={option.value}
                                checked={formState.shakl === option.value}
                                onChange={handleFieldChange}
                              />
                              <span className="psi-passport-shape-option__glyph">
                                <img
                                  src={resolveAsset(option.asset)}
                                  alt={option.label}
                                  className="psi-passport-shape-option__image"
                                />
                              </span>
                              <span className="psi-passport-shape-option__label">{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="xarakter">Xarakter</Label>
                        <Input
                          id="xarakter"
                          name="xarakter"
                          type="select"
                          value={formState.xarakter}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        >
                          {XARAKTER_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="rang">Yoqtirgan rang</Label>
                        <Input
                          id="rang"
                          name="rang"
                          value={formState.rang}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="salomatlik">Salomatligi haqida umumiy ma`lumot</Label>
                        <Input
                          id="salomatlik"
                          name="salomatlik"
                          type="textarea"
                          rows="3"
                          value={formState.salomatlik}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="ozlashtirish">Fanlarni o`zlashtirishi</Label>
                        <Input
                          id="ozlashtirish"
                          name="ozlashtirish"
                          type="textarea"
                          rows="3"
                          value={formState.ozlashtirish}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="munosabat">Oilaviy munosabatlar xususiyatlari</Label>
                        <Input
                          id="munosabat"
                          name="munosabat"
                          type="textarea"
                          rows="3"
                          value={formState.munosabat}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="kursdosh">Kursdoshlari bilan munosabati</Label>
                        <Input
                          id="kursdosh"
                          name="kursdosh"
                          type="textarea"
                          rows="3"
                          value={formState.kursdosh}
                          onChange={handleFieldChange}
                          className="psi-passport-input"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>

              <div className="psi-passport-photo">
                <img src={photoUrl} alt={formState.full_name || 'Talaba rasmi'} className="psi-passport-photo__image" />
                <div className="psi-passport-photo__meta">
                  <Badge pill className="psi-passport-state psi-passport-state--success">
                    Student portal
                  </Badge>
                  <Label for="student-passport-image-upload" className="psi-passport-photo__button">
                    Rasmni yangilash
                  </Label>
                  <Input
                    id="student-passport-image-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />
                  <span className="psi-passport-photo__hint">Maksimal hajm: 1 MB. Rasm talaba pasportiga biriktiriladi.</span>

                  <div className="psi-student-passport__actions">
                    <Button color="primary" className="psi-login-submit" onClick={handleSave} disabled={saving || logoutPending}>
                      {saving || logoutPending ? (
                        <Spinner size="sm" className="mr-2" />
                      ) : (
                        <FontAwesomeIcon icon="check-circle" className="mr-2" />
                      )}
                      {logoutPending ? 'Chiqilmoqda...' : 'Saqlash'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StudentPassportPage;
